import React from 'react';
import { NODE_TYPES_CONFIG } from './WorkflowNode';

const inputClasses = 'w-full bg-card border border-white/10 rounded-md px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary';
const selectClasses = inputClasses;
const labelClasses = 'block text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1';

function MarketDataProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Symbol</label>
        <input
          type="text"
          value={config.symbol || ''}
          onChange={(e) => onChange({ ...config, symbol: e.target.value.toUpperCase() })}
          placeholder="e.g., AAPL, BTC/USD"
          className={inputClasses}
        />
      </div>
      <div>
        <label className={labelClasses}>Data Type</label>
        <select value={config.dataType || 'price'} onChange={(e) => onChange({ ...config, dataType: e.target.value })} className={selectClasses}>
          <option value="price">Price (Quote)</option>
          <option value="bars">Historical Bars</option>
          <option value="orderbook">Order Book</option>
          <option value="trades">Recent Trades</option>
        </select>
      </div>
      <div>
        <label className={labelClasses}>Provider</label>
        <select value={config.provider || 'default'} onChange={(e) => onChange({ ...config, provider: e.target.value })} className={selectClasses}>
          <option value="default">Default (User's Primary)</option>
          <option value="yahoo">Yahoo Finance</option>
          <option value="alpaca">Alpaca</option>
        </select>
      </div>
      {config.dataType === 'bars' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelClasses}>Timeframe</label>
            <select value={config.timeframe || '1d'} onChange={(e) => onChange({ ...config, timeframe: e.target.value })} className={selectClasses}>
              <option value="1m">1 Min</option>
              <option value="5m">5 Min</option>
              <option value="15m">15 Min</option>
              <option value="1h">1 Hour</option>
              <option value="1d">1 Day</option>
            </select>
          </div>
          <div>
            <label className={labelClasses}>Bars</label>
            <input type="number" value={config.barCount || 30} onChange={(e) => onChange({ ...config, barCount: parseInt(e.target.value, 10) })} min={5} max={500} className={inputClasses} />
          </div>
        </div>
      )}
    </>
  );
}

function StrategyStatusProps({ config, onChange }) {
  return (
    <div>
      <label className={labelClasses}>Strategy ID</label>
      <input type="text" value={config.strategyId || ''} onChange={(e) => onChange({ ...config, strategyId: e.target.value })} placeholder="Strategy UUID" className={inputClasses} />
      <p className="text-[10px] text-muted-foreground/50 mt-1">Fetches performance, positions, and P&L</p>
    </div>
  );
}

function TimerProps({ config, onChange }) {
  return (
    <div>
      <label className={labelClasses}>Interval (minutes)</label>
      <input type="number" value={config.intervalMinutes || 5} onChange={(e) => onChange({ ...config, intervalMinutes: parseInt(e.target.value, 10) })} min={1} max={1440} className={inputClasses} />
      <p className="text-[10px] text-muted-foreground/50 mt-1">How often this branch triggers via MonitorLoop</p>
    </div>
  );
}

function AIAnalysisProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Prompt / Guidelines</label>
        <textarea
          value={config.prompt || ''}
          onChange={(e) => onChange({ ...config, prompt: e.target.value })}
          placeholder="Analyze the current market conditions and suggest adjustments..."
          rows={4}
          className={`${inputClasses} resize-y min-h-[60px]`}
        />
      </div>
      <div>
        <label className={labelClasses}>Model</label>
        <div className="flex gap-1.5">
          {['haiku', 'sonnet'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onChange({ ...config, model: m })}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                config.model === m ? 'bg-primary/15 text-primary border border-primary/20' : 'text-muted-foreground border border-white/5 hover:bg-accent/10'
              }`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelClasses}>Temperature</label>
        <input type="range" min="0" max="1" step="0.1" value={config.temperature ?? 0.3} onChange={(e) => onChange({ ...config, temperature: parseFloat(e.target.value) })} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-accent/20 accent-primary" />
        <p className="text-[10px] text-muted-foreground/50 text-right">{config.temperature ?? 0.3}</p>
      </div>
      <div>
        <label className={labelClasses}>Max Tokens</label>
        <select value={config.maxTokens || 4096} onChange={(e) => onChange({ ...config, maxTokens: parseInt(e.target.value, 10) })} className={selectClasses}>
          <option value={1024}>1,024</option>
          <option value={2048}>2,048</option>
          <option value={4096}>4,096</option>
          <option value={8192}>8,192</option>
        </select>
      </div>
    </>
  );
}

function IndicatorCheckProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Indicator</label>
        <select value={config.indicator || 'atr'} onChange={(e) => onChange({ ...config, indicator: e.target.value })} className={selectClasses}>
          <option value="atr">ATR (Average True Range)</option>
          <option value="rsi">RSI (Relative Strength Index)</option>
          <option value="sma">SMA (Simple Moving Average)</option>
          <option value="ema">EMA (Exponential Moving Average)</option>
          <option value="bollinger">Bollinger Bands</option>
          <option value="macd">MACD</option>
          <option value="volume">Volume</option>
        </select>
      </div>
      <div>
        <label className={labelClasses}>Period</label>
        <input type="number" value={config.period || 14} onChange={(e) => onChange({ ...config, period: parseInt(e.target.value, 10) })} min={2} max={200} className={inputClasses} />
      </div>
      {config.indicator === 'bollinger' && (
        <div>
          <label className={labelClasses}>Std Dev Multiplier</label>
          <input type="number" value={config.stdDev || 2} onChange={(e) => onChange({ ...config, stdDev: parseFloat(e.target.value) })} min={0.5} max={5} step={0.1} className={inputClasses} />
        </div>
      )}
    </>
  );
}

function ThresholdProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Field</label>
        <select value={config.field || 'price'} onChange={(e) => onChange({ ...config, field: e.target.value })} className={selectClasses}>
          <option value="price">Price</option>
          <option value="rsi">RSI Value</option>
          <option value="atr">ATR Value</option>
          <option value="volume">Volume</option>
          <option value="pnl">Unrealized P&L (%)</option>
          <option value="drawdown">Drawdown (%)</option>
          <option value="return">Total Return (%)</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelClasses}>Operator</label>
          <select value={config.operator || '>'} onChange={(e) => onChange({ ...config, operator: e.target.value })} className={selectClasses}>
            <option value=">">&gt; Greater than</option>
            <option value="<">&lt; Less than</option>
            <option value=">=">&ge; Greater or equal</option>
            <option value="<=">&le; Less or equal</option>
            <option value="==">== Equal</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>Value</label>
          <input type="number" value={config.value ?? 0} onChange={(e) => onChange({ ...config, value: parseFloat(e.target.value) })} step="any" className={inputClasses} />
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground/50">True branch (green) taken when condition is met, False branch (red) otherwise.</p>
    </>
  );
}

function IfElseProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Condition Expression</label>
        <textarea
          value={config.condition || ''}
          onChange={(e) => onChange({ ...config, condition: e.target.value })}
          placeholder="e.g., data.rsi > 70 && data.price > data.sma"
          rows={3}
          className={`${inputClasses} resize-y min-h-[50px] font-mono`}
        />
        <p className="text-[10px] text-muted-foreground/50 mt-1">JavaScript expression evaluated against upstream data context</p>
      </div>
    </>
  );
}

function AdjustStrategyProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Strategy ID</label>
        <input type="text" value={config.strategyId || ''} onChange={(e) => onChange({ ...config, strategyId: e.target.value })} placeholder="Strategy UUID" className={inputClasses} />
      </div>
      <div>
        <label className={labelClasses}>Parameter Overrides (JSON)</label>
        <textarea
          value={typeof config.parameters === 'string' ? config.parameters : JSON.stringify(config.parameters || {}, null, 2)}
          onChange={(e) => {
            try { onChange({ ...config, parameters: JSON.parse(e.target.value) }); } catch { /* allow invalid JSON while typing */ }
          }}
          placeholder='{ "orderSize": 150, "gridLevels": 15 }'
          rows={4}
          className={`${inputClasses} resize-y min-h-[60px] font-mono`}
        />
        <p className="text-[10px] text-muted-foreground/50 mt-1">Merged with existing strategy parameters</p>
      </div>
    </>
  );
}

function StartStopProps({ config, onChange }) {
  return (
    <>
      <div>
        <label className={labelClasses}>Strategy ID</label>
        <input type="text" value={config.strategyId || ''} onChange={(e) => onChange({ ...config, strategyId: e.target.value })} placeholder="Strategy UUID" className={inputClasses} />
      </div>
      <div>
        <label className={labelClasses}>Action</label>
        <div className="flex gap-1.5">
          {[
            { value: 'start', label: 'Start', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
            { value: 'stop', label: 'Stop', color: 'bg-red-500/15 text-red-400 border-red-500/20' },
            { value: 'pause', label: 'Pause', color: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
          ].map((a) => (
            <button
              key={a.value}
              type="button"
              onClick={() => onChange({ ...config, action: a.value })}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                config.action === a.value ? a.color : 'text-muted-foreground border-white/5 hover:bg-accent/10'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function NotifyProps({ config, onChange }) {
  return (
    <div>
      <label className={labelClasses}>Message Template</label>
      <textarea
        value={config.message || ''}
        onChange={(e) => onChange({ ...config, message: e.target.value })}
        placeholder="Strategy {{strategyName}} triggered: {{reason}}"
        rows={3}
        className={`${inputClasses} resize-y min-h-[50px]`}
      />
      <p className="text-[10px] text-muted-foreground/50 mt-1">Use {'{{field}}'} for dynamic values from upstream data</p>
    </div>
  );
}

const PROPERTY_EDITORS = {
  market_data: MarketDataProps,
  strategy_status: StrategyStatusProps,
  timer: TimerProps,
  ai_analysis: AIAnalysisProps,
  indicator_check: IndicatorCheckProps,
  threshold: ThresholdProps,
  if_else: IfElseProps,
  adjust_strategy: AdjustStrategyProps,
  start_stop: StartStopProps,
  notify: NotifyProps,
};

export default function NodePropertiesPanel({ node, onConfigChange }) {
  if (!node) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-muted-foreground/50">Select a node to edit its properties</p>
      </div>
    );
  }

  const { nodeType, config, label, icon } = node.data;
  const typeCfg = NODE_TYPES_CONFIG[nodeType] || {};
  const Editor = PROPERTY_EDITORS[nodeType];

  return (
    <div className="space-y-3">
      {/* Node header */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${typeCfg.color || 'border-white/10'}`}>
        <span className="text-base">{icon}</span>
        <div>
          <p className="text-xs font-semibold text-foreground">{label}</p>
          <p className="text-[10px] text-muted-foreground capitalize">{typeCfg.category} node</p>
        </div>
      </div>

      {/* Node ID */}
      <div>
        <p className="text-[10px] text-muted-foreground/40 font-mono truncate">{node.id}</p>
      </div>

      {/* Properties form */}
      {Editor ? (
        <Editor config={config || {}} onChange={onConfigChange} />
      ) : (
        <p className="text-xs text-muted-foreground/50">No configurable properties</p>
      )}
    </div>
  );
}
